import { objectFilterTruly, truly } from 'shared/utils/types'
import { useOrderFlatware } from '@/services/order'
import { Product } from '@/components/product'
import { useSelectedPreferences, augmentProductsByPreferences } from '@/services/preferences'
import Link from 'next/link'
import { useExcludedElements } from '@/services/excluded-elements'
import { TrashIcon } from '@/components/trash-icon'
import { join, unique, sum, keys } from 'shared/utils/array'
import React, { useMemo, PropsWithChildren } from 'react'
import { OrderedProducts } from '@/components/ordered-products'
import { useRouter } from 'next/router'
import { Spinner } from '@/components/spinner'
import { Product as TProduct } from 'shared/types/api'
import { AppInput } from '@/components/input'
import { PersonalDataConsent } from '@/components/personal-data-consent'
import { OrderDeliveryPrice } from '@/components/order-delivery-price'
import { useVenueContext } from '@/services/venue'
import { usePrice } from '@/components/price'
import { DeleteButton } from '@/components/delete-button'
import { SCROLL_TO_MODIFIERS_QUERY_PARAM } from '../pages/venue/[venueName]/p/[productGroupName]/[productName]'
import { useAppContext } from '@/pages/_app'
import { Trans } from 'react-i18next'
import { venueGetOrderingTypes } from 'shared/venue'
import { useEffect$ } from '@/utils/event'
import { CheckoutViewController, getMinOrderPriceIfNotReached } from '@/services/checkout'
import { useObservable } from 'rxjs-hooks'
import { map, pairwise } from 'rxjs'
import { ChevronIcon } from './chevron-icon'

export function CheckoutView(props: { controller: CheckoutViewController }) {
  const { t } = useAppContext()
  const router = useRouter()
  const venueContext = useVenueContext()!
  const {
    venue,
    venueName,
    orderManager,
    menuRoute,
    availablePreferences,
    orderTypeHasPaymentMethods,
  } = venueContext

  const price = usePrice()
  const { basket, deliveryMapManager } = orderManager
  const orderingTypes = venueGetOrderingTypes(venue.features)
  const currentOrderMode = orderManager.orderMode
  const selectedPreferences = useSelectedPreferences()
  const excludedElements = useExcludedElements(venueName)
  const flatware = useOrderFlatware()
  const {
    setShowMapScreen,
    mapScreenRef,
    chosenMapAddressImplicit,
    requestDeliveryAddressIfNeeded,
  } = deliveryMapManager

  const serviceType = venue.features.serviceType || 'waiter'
  const paymentMethods = orderingTypes[currentOrderMode.value]?.paymentMethods || {}
  const paymentMethodsFiltered = objectFilterTruly({
    ...paymentMethods,
    online: paymentMethods.online && venue.paymentGateway ? paymentMethods.online : undefined,
  })

  const mapProvider = orderingTypes.delivery?.mapProvider
  const mapProviderType = mapProvider?.type

  const isProductInBasket = (product: TProduct) => {
    return (
      sum(
        basket.products
          .filter({
            productName: product.name,
            productGroupName: product.productGroupName,
          })
          .map((p) => {
            return p.quantity
          }),
      ) > 0
    )
  }

  const productUpsaleProducts = useMemo(() => {
    return unique(
      augmentProductsByPreferences(
        venue.productGroups.flatMap((pg) => {
          return pg.products.filter(isProductInBasket).flatMap((p) => {
            return p.upsale.products
              .map(({ productName, productGroupName }) => {
                return venue.productGroups
                  .find((pg) => {
                    return pg.name === productGroupName
                  })
                  ?.products.find((product) => {
                    return product.name === productName
                  })
              })
              .filter(truly)
          })
        }),
        availablePreferences,
        selectedPreferences,
      ).filter((product) => {
        return (
          !isProductInBasket(product) &&
          (!selectedPreferences.hasPreferences || product.fitDescription.productFitsPreferences)
        )
      }),
      (product) => {
        return product.productGroupName + product.name
      },
    )
  }, [basket.size === 0, venue])

  const orderedProducts = basket.products
    .getAll()
    .map((basketProduct) => {
      if (basketProduct.quantity === 0) {
        return null
      }

      const productGroup = venue.productGroups.find((pg) => {
        return pg.name === basketProduct.productGroupName
      })
      const product = productGroup?.products.find((p) => {
        return p.name === basketProduct.productName
      })

      if (!product) {
        return null
      }

      const productWithFitDescription = augmentProductsByPreferences(
        [product],
        availablePreferences,
        selectedPreferences,
      )[0]!

      return {
        product: productWithFitDescription,
        fitDescription: productWithFitDescription.fitDescription,
        quantity: basketProduct.quantity,
        appliedModifiers: basketProduct.modifiers,
        basketProductId: basketProduct.__id,
      }
    })
    .filter(truly)

  const useTableNumber = venue.features.orderingTypes?.onSite?.useTableNumber

  const basketPrices$ = useEffect$([basket.prices]).pipe(
    map((v) => {
      return v[0]
    }),
    pairwise(),
  )
  const basketPrices = useObservable(() => {
    return basketPrices$
  })

  return (
    <div className="pb-12">
      {orderTypeHasPaymentMethods && orderedProducts.length > 0 ? (
        <>
          {(() => {
            const [prevBasketPrices, curBasketPrices] = basketPrices ?? []
            const bp = curBasketPrices === 'loading' ? prevBasketPrices : curBasketPrices
            // const minOrderPriceToAllowDelivery = curMinPrice ?? prevMinPrice
            const minOrderPriceToAllowDelivery = getMinOrderPriceIfNotReached(bp ?? basket.prices)
            if (minOrderPriceToAllowDelivery) {
              return (
                <div className="mb-6 bg-backNegative dark:bg-backNegativeDark px-4 py-5 text-lg leading-tight">
                  {t('checkout:minAmountExcludingDeliveryCosts')} –{' '}
                  {price.format(minOrderPriceToAllowDelivery.threshold)}
                </div>
              )
            }
            return (
              <div className="mb-3 bg-backAccent dark:bg-backAccentDark px-4 py-5 text-lg leading-tight">
                {(() => {
                  if (currentOrderMode.value === 'delivery') {
                    return t('checkout:pleaseCheckOrderAndProvideDeliveryAddress')
                  } else {
                    if (
                      paymentMethodsFiltered.online &&
                      keys(paymentMethodsFiltered).length === 1
                    ) {
                      return t('checkout:youWillGetQueueNumberAfterPayment')
                    }

                    if (keys(paymentMethodsFiltered).length > 1) {
                      return t('checkout:pleaseCheckOrderAndMakePayment')
                    }

                    return t('checkout:pleaseCheckOrderAndPressButton')
                  }
                })()}
              </div>
            )
          })()}
        </>
      ) : null}
      {props.controller.productListMode.type === 'delete' && orderedProducts.length > 0 ? (
        <div className="mt-1 mx-5">
          <DeleteButton
            onClick={() => {
              basket.products.clear()
              props.controller.actions.toggleDeleteMode()
            }}
          >
            <>
              <TrashIcon color="stroke-option" />
              <span className="ml-1">{t('checkout:clearAll')}</span>
            </>
          </DeleteButton>
        </div>
      ) : null}
      {currentOrderMode.value !== 'onSite' && flatware.isConfigurable && basket.size > 0 ? (
        <div className="px-4 mt-3">
          <div className="flex justify-between items-center text-lg">
            <span>{t('common:flatware')}</span>
            <div className="flex items-center text-lg text-center h-10">
              <span
                className="flex items-center justify-center w-10 h-full font-bold text-opaque dark:text-opaqueDark rounded-lg bg-backOpaque dark:bg-backOpaqueDark cursor-pointer select-none"
                onClick={(e) => {
                  e.preventDefault()
                  flatware.quantity.set(Math.max(flatware.quantity.value - 1, 0))
                }}
              >
                —
              </span>
              <span className="flex items-center justify-center w-10 h-full mx-2 text-center">
                {flatware.quantity.value}
              </span>
              <span
                className="flex items-center justify-center w-10 h-full font-bold text-opaque dark:text-opaqueDark rounded-lg bg-backOpaque dark:bg-backOpaqueDark cursor-pointer select-none"
                onClick={(e) => {
                  e.preventDefault()
                  flatware.quantity.set(flatware.quantity.value + 1)
                }}
              >
                ＋
              </span>
            </div>
          </div>
          <div className="my-4 border-b border-ghost dark:border-ghostDark">
            {/* horizontal separator */}
          </div>
        </div>
      ) : null}
      <div className="mt-4">
        {currentOrderMode.value === 'delivery' && basket.size > 0 ? (
          <>
            {(mapProviderType === 'yandex' || mapProviderType === 'mapbox') && (
              <div className="mx-4">
                <div
                  className="flex justify-between text-lg items-center cursor-pointer"
                  onClick={() => {
                    if (chosenMapAddressImplicit) {
                      mapScreenRef.current!.chooseAddress(chosenMapAddressImplicit.coords)
                    }
                    setShowMapScreen(true)
                  }}
                >
                  <span className="leading-6">
                    {chosenMapAddressImplicit?.text || t('common:enterDeliveryAddress')}
                  </span>
                  <span className="ml-4">
                    <ChevronIcon />
                  </span>
                </div>
                <div className="my-4 border-b border-ghost dark:border-ghostDark">
                  {/* horizontal separator */}
                </div>
              </div>
            )}

            {
              // Show delivery price only when the address is selected, or always if mapProviderType is "input"
              (((mapProviderType === 'yandex' || mapProviderType === 'mapbox') &&
                chosenMapAddressImplicit) ||
                mapProviderType === 'input') && (
                <OrderDeliveryPrice
                  orderPrices={basket.prices}
                  orderIsPaid={false}
                  showNextTier={!getMinOrderPriceIfNotReached(basket.prices)}
                />
              )
            }
          </>
        ) : null}
        {(() => {
          if (orderedProducts.length === 0) {
            return (
              <Link href={menuRoute.path} legacyBehavior>
                <div className="px-4 text-opaque dark:text-opaqueDark leading-tight cursor-pointer">
                  <Trans
                    t={t}
                    i18nKey="orderedProducts:yourBasketIsEmpty"
                    components={{
                      p1: <p className="mb-2" />,
                      menuLink: <a className="underline" />,
                    }}
                  />
                </div>
              </Link>
            )
          }

          return (
            <OrderedProducts
              isDeleteMode={props.controller.productListMode.type === 'delete'}
              hasPreferences={selectedPreferences.hasPreferences}
              onProductDelete={(orderedProduct) => {
                basket.products.deleteById(orderedProduct.basketProductId!)
                if (basket.size === 1) {
                  props.controller.actions.toggleDeleteMode()
                }
              }}
              orderedProducts={orderedProducts}
              orderPrices={basket.prices}
              excludedElements={excludedElements}
              style={!orderTypeHasPaymentMethods ? 'simple' : 'detailed'}
              attachProductLinks
            />
          )
        })()}
      </div>
      {currentOrderMode.value === 'onSite' &&
        orderTypeHasPaymentMethods &&
        orderedProducts.length > 0 && (
          <div className="px-4 mt-2">
            <span className="text-lg">{t('checkout:provideYourDetails')}</span>
            {useTableNumber && (
              <div className="mt-2">
                <AppInput {...props.controller.inputs.tableNumber} />
              </div>
            )}
            {!!paymentMethodsFiltered.online && (
              <div className="mt-2">
                <AppInput {...props.controller.inputs.phone} />
              </div>
            )}
            {venue.paymentGateway?.type === 'yoo' &&
              venue.paymentGateway?.checks &&
              !!paymentMethodsFiltered.online && (
                <div className="mt-2">
                  <AppInput {...props.controller.inputs.email} />
                </div>
              )}
            <div className="mt-3">
              <AppInput {...props.controller.inputs.onSiteComment} />
            </div>
            {!!paymentMethodsFiltered.online && (
              <div className="mt-2">
                <PersonalDataConsent {...{ venue }} />
              </div>
            )}
          </div>
        )}
      {currentOrderMode.value === 'takeaway' && orderedProducts.length > 0 ? (
        <div className="px-4 mt-2">
          <span className="text-lg">{t('checkout:provideYourDetails')}</span>
          <div className="mt-2">
            <AppInput {...props.controller.inputs.phone} />
          </div>
          <div className="mt-2">
            <AppInput {...props.controller.inputs.name} />
          </div>
          {venue.paymentGateway?.type === 'yoo' &&
            venue.paymentGateway?.checks &&
            !!paymentMethodsFiltered.online && (
              <div className="mt-2">
                <AppInput {...props.controller.inputs.email} />
              </div>
            )}
          <div className="mt-3">
            <AppInput {...props.controller.inputs.takeawayComment} />
          </div>
          <div className="mt-2">
            <PersonalDataConsent {...{ venue }} />
          </div>
        </div>
      ) : null}
      {!orderTypeHasPaymentMethods && basket.size > 0 && (
        <div className="mt-2 mb-4 bg-backAccent dark:bg-backAccentDark px-4 py-5 text-lg leading-tight custom-discuss-your-list-and-make-order">
          {t('checkout:discussYourListAndMakeOrder', {
            person: serviceType === 'waiter' ? t('checkout:withWaiter') : t('checkout:withCashier'),
          })}
        </div>
      )}
      {productUpsaleProducts.length > 0 && (
        <div className="my-4">
          <div className="my-4 border-4 border-backScreen dark:border-backScreenDark">
            {/* horizontal separator */}
          </div>
          <div className="px-4 my-2">
            <h2 className="inline font-medium text-2xl">{t('checkout:addToTheOrder')}</h2>
            <span className="inline-block w-2">{/* gap */}</span>
            <span className="w-2 font-medium text-opaque dark:text-opaqueDark text-lg">
              {productUpsaleProducts.length}
            </span>
          </div>
          {join(
            productUpsaleProducts.map((product) => {
              return (
                <Product
                  key={product.name + product.productGroupName}
                  filtersUsed={selectedPreferences.hasPreferences}
                  product={product}
                  sourceProductGroupName={product.productGroupName}
                  venueName={venueName}
                  allowMovePriceToOrderButton={false}
                  orderedQuantity={sum(
                    basket.products
                      .filter({
                        productGroupName: product.productGroupName,
                        productName: product.name,
                      })
                      .map((p) => {
                        return p.quantity
                      }),
                  )}
                  onQuantityChange={(quantity, change, e) => {
                    if (requestDeliveryAddressIfNeeded(e.target) === 'requested') {
                      return
                    }

                    if (product.modifierGroups.length > 0) {
                      if (change === '+') {
                        return router.push(
                          `/venue/${venueName}/p/${product.productGroupName}/${product.name}?${SCROLL_TO_MODIFIERS_QUERY_PARAM}`,
                        )
                      }

                      const latestBasketProduct = basket.products
                        .filter({
                          productGroupName: product.productGroupName,
                          productName: product.name,
                        })
                        .filter((p) => {
                          return p.quantity > 0
                        })
                        .sort((a, b) => {
                          return b.createdAt - a.createdAt
                        })[0]

                      if (latestBasketProduct) {
                        basket.products.set(latestBasketProduct.__id, {
                          quantity: latestBasketProduct.quantity - 1,
                        })
                      }

                      return
                    }
                    basket.products.setQuantity(
                      {
                        productGroupName: product.productGroupName,
                        productName: product.name,
                        modifiers: {},
                      },
                      quantity,
                    )
                  }}
                />
              )
            }),
            <div className="px-4">
              <div className="my-4 border-b border-ghost dark:border-ghostDark">
                {/* horizontal separator */}
              </div>
            </div>,
          )}
        </div>
      )}
    </div>
  )
}

export function CheckoutSubmitButton(props: { controller: CheckoutViewController }) {
  const { actionState } = props.controller
  switch (actionState.type) {
    case 'disabled':
      return (
        <ActionButton className="cursor-not-allowed text-opaque dark:text-opaqueDark">
          {actionState.text}
        </ActionButton>
      )
    case 'error':
      return (
        <ActionButton className="cursor-not-allowed text-negative dark:text-negativeDark">
          {actionState.text}
        </ActionButton>
      )
    case 'loading':
      return (
        <ActionButton className="cursor-wait">
          <div className="w-6 inline-block mx-auto">
            <Spinner />
          </div>
        </ActionButton>
      )
    case 'onClick':
      return <ActionButton onClick={actionState.onClick}>{actionState.text}</ActionButton>
    case 'submit':
      return <ActionButton submit>{actionState.text}</ActionButton>
  }
}

export function CheckoutPaymentMethodsOverlay(props: {
  controller: CheckoutViewController
  width: () => number
}) {
  const { t } = useAppContext()
  const { venue, orderManager } = useVenueContext()!
  const { orderMode } = orderManager
  const serviceType = venue.features.serviceType || 'waiter'

  return (
    <div
      className="fixed inset-y-0 inset-x-auto h-full z-40 px-5 pb-20 flex flex-col justify-end"
      style={{
        background: 'rgba(0, 0, 0, 0.8)',
        display: props.controller.orderState.type === 'selectingPaymentMethod' ? 'flex' : 'none',
        width: props.width(),
      }}
      onClick={() => {
        return props.controller.actions.closePaymentMethodSelector()
      }}
    >
      {keys(props.controller.paymentMethodsFiltered).map((paymentMethod) => {
        return (
          <div
            key={paymentMethod}
            className="flex px-4 mb-3 justify-center items-center h-12 text-lg rounded-lg bg-back dark:bg-backDark cursor-pointer font-medium"
            onClick={(event) => {
              event.stopPropagation()
              props.controller.actions.selectPaymentMethod({ type: paymentMethod })
            }}
          >
            {(() => {
              switch (paymentMethod) {
                case 'online':
                  return t('common:cardOnline')
                case 'cashier': {
                  switch (orderMode.value) {
                    case 'onSite':
                      switch (serviceType) {
                        case 'cashier':
                          return t('checkout:atTheCounter')
                        case 'waiter':
                          return t('checkout:toTheWaiter')
                      }
                    case 'takeaway':
                      return t('checkout:uponReceipt')
                  }
                }
              }
            })()}
          </div>
        )
      })}
    </div>
  )
}

function ActionButton(
  props: PropsWithChildren<{ className?: string; submit?: boolean; onClick?: () => void }>,
) {
  return (
    <button
      type={props.submit ? 'submit' : 'button'}
      className={`${
        props.className ?? ''
      } w-full flex px-4 items-center h-12 text-lg rounded-lg bg-back dark:bg-backAccentDark dark:text-primaryAccentDark cursor-pointer font-medium`}
      onClick={(event) => {
        if (props.onClick) {
          event.preventDefault()
          props.onClick && props.onClick()
        }
      }}
    >
      <span className="w-full text-center leading-none">{props.children}</span>
    </button>
  )
}
